import React, { useEffect, useState } from "react";

interface PlatformColorsProps {
  onPrimaryColorChange?: (color: string) => void;
  selectedPrimaryColor?: string;
}

const COLOR_PALETTE = [
  // Azuis
  { name: "Azul SoulHub", value: "#3B6EF5" },
  { name: "Azul Royal", value: "#1e40af" },
  { name: "Azul Céu", value: "#0ea5e9" },
  { name: "Azul Marinho", value: "#1e3a8a" },
  { name: "Azul Cobalto", value: "#1d4ed8" },
  { name: "Azul Turquesa", value: "#0891b2" },
  { name: "Azul Índigo", value: "#4338ca" },
  { name: "Azul Dodger", value: "#1e90ff" },
  { name: "Azul Petróleo", value: "#0f766e" },
  { name: "Azul Meia-Noite", value: "#191970" },
  { name: "Azul Aço", value: "#4682b4" },
  { name: "Azul Claro", value: "#3b82f6" },

  // Roxos/Violetas
  { name: "Roxo", value: "#6d4f97" },
  { name: "Violeta", value: "#8b5cf6" },
  { name: "Roxo Escuro", value: "#5b21b6" },
  { name: "Roxo Profundo", value: "#7c3aed" },
  { name: "Magenta", value: "#c026d3" },
  { name: "Púrpura", value: "#9333ea" },
  { name: "Violeta Escuro", value: "#9400d3" },
  { name: "Roxo Médio", value: "#9370db" },

  // Verdes
  { name: "Verde", value: "#059669" },
  { name: "Verde Esmeralda", value: "#10b981" },
  { name: "Verde Escuro", value: "#047857" },
  { name: "Verde Lima", value: "#65a30d" },
  { name: "Verde Floresta", value: "#166534" },
  { name: "Verde Mar", value: "#2e8b57" },

  // Vermelhos/Rosas
  { name: "Vermelho", value: "#dc2626" },
  { name: "Rosa", value: "#e11d48" },
  { name: "Rosa Quente", value: "#ec4899" },
  { name: "Vermelho Escarlate", value: "#ef4444" },

  // Laranjas/Amarelos
  { name: "Laranja", value: "#ea580c" },
  { name: "Laranja Suave", value: "#fb923c" },
  { name: "Amarelo Dourado", value: "#eab308" },
  { name: "Amarelo", value: "#d97706" },

  // Cinzas/Neutros
  { name: "Cinza Escuro", value: "#374151" },
  { name: "Preto", value: "#111827" },
  { name: "Cinza Médio", value: "#4b5563" },
  { name: "Cinza Aço", value: "#64748b" },

  // Especiais
  { name: "Coral", value: "#ff7f50" },
  { name: "Terracota", value: "#e2725b" },
  { name: "Bronze", value: "#cd7f32" },
  { name: "Marrom", value: "#8b4513" },
];

function isLightColor(hex: string): boolean {
  const h = hex.replace("#", "");
  const r = parseInt(h.substr(0, 2), 16);
  const g = parseInt(h.substr(2, 2), 16);
  const b = parseInt(h.substr(4, 2), 16);
  return (r * 299 + g * 587 + b * 114) / 1000 > 128;
}

export function PlatformColors({
  onPrimaryColorChange,
  selectedPrimaryColor,
}: PlatformColorsProps) {
  const defaultColor = "#3B6EF5";
  const [color, setColor] = useState(selectedPrimaryColor || defaultColor);
  const [activeTab, setActiveTab] = useState<"preset" | "custom">("preset");
  const [tempHex, setTempHex] = useState(selectedPrimaryColor || defaultColor);

  useEffect(() => {
    if (selectedPrimaryColor && selectedPrimaryColor !== color) {
      setColor(selectedPrimaryColor);
      setTempHex(selectedPrimaryColor);
    }
  }, [selectedPrimaryColor]);

  const apply = (value: string) => {
    setColor(value);
    setTempHex(value);
    onPrimaryColorChange?.(value);
  };

  return (
    <div className="flex flex-col gap-6">
      {/* Preview compacto */}
      <div className="flex items-center gap-4">
        <div
          className="w-14 h-14 rounded-xl border-2 border-[#353535] shadow-lg flex-shrink-0"
          style={{ backgroundColor: color }}
        />
        <div>
          <p className="text-sm font-semibold text-white">Cor primária</p>
          <p className="text-xs text-[#787878] font-mono">{color.toUpperCase()}</p>
          <p className="text-xs text-[#787878] mt-0.5">Usada em botões, ícones e destaques</p>
        </div>

        {/* Mini preview da LP */}
        <div
          className="ml-auto rounded-lg border border-[#353535] px-4 py-3 min-w-[180px] text-center"
          style={{ background: `linear-gradient(135deg, ${color}0D 0%, #ffffff 100%)` }}
        >
          <p className="text-xs font-bold mb-1" style={{ color }}>Proteja sua Família</p>
          <p className="text-[10px] text-gray-500 mb-2">Planos acessíveis para você</p>
          <div
            className="inline-block text-[10px] font-semibold px-3 py-1 rounded-full text-white"
            style={{ backgroundColor: color }}
          >
            Ver Planos
          </div>
        </div>
      </div>

      {/* Abas */}
      <div className="bg-[#1a1a1a] rounded-xl border border-[#353535] overflow-hidden">
        <div className="flex border-b border-[#353535]">
          {(["preset", "custom"] as const).map((tab) => (
            <button
              key={tab}
              type="button"
              onClick={() => setActiveTab(tab)}
              className={`flex-1 px-4 py-2.5 text-sm font-medium transition-colors ${
                activeTab === tab
                  ? "text-white border-b-2 border-blue-500"
                  : "text-[#787878] hover:text-white"
              }`}
            >
              {tab === "preset" ? "Cores predefinidas" : "Cor personalizada"}
            </button>
          ))}
        </div>

        <div className="p-4">
          {activeTab === "preset" ? (
            <div className="grid grid-cols-10 gap-2">
              {COLOR_PALETTE.map((c) => (
                <button
                  key={c.value}
                  type="button"
                  title={`${c.name} — ${c.value}`}
                  className="w-8 h-8 rounded-lg border-2 transition-all duration-150 hover:scale-110 focus:outline-none focus:ring-2 focus:ring-white/30"
                  style={{
                    backgroundColor: c.value,
                    borderColor: color === c.value ? "#ffffff" : "rgba(255,255,255,0.15)",
                    boxShadow: color === c.value ? "0 0 0 2px #3B6EF5" : undefined,
                  }}
                  onClick={() => apply(c.value)}
                />
              ))}
            </div>
          ) : (
            <div className="flex items-center gap-4">
              <input
                type="color"
                value={tempHex}
                onChange={(e) => setTempHex(e.target.value)}
                className="w-12 h-12 rounded border border-[#353535] cursor-pointer"
              />
              <input
                type="text"
                value={tempHex.toUpperCase()}
                onChange={(e) => {
                  const v = e.target.value;
                  if (/^#[0-9A-Fa-f]{0,6}$/.test(v)) setTempHex(v);
                }}
                className="w-28 h-9 px-3 bg-[#191919] border border-[#353535] rounded-lg text-white text-sm font-mono focus:outline-none focus:ring-2 focus:ring-blue-500/50"
                placeholder="#3B6EF5"
                maxLength={7}
              />
              <button
                type="button"
                onClick={() => apply(tempHex)}
                disabled={!/^#[0-9A-Fa-f]{6}$/.test(tempHex)}
                className="px-4 py-1.5 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-40 transition-colors"
              >
                Aplicar
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
